<template>
  <TravelersFilterPopup
    ref="calendarPopup"
    filter-key="dateNoPersons"
    :title
    :static-title="staticTitle"
    :is-active="categoryStore.isFilterActive(EExperienceFilterType.PERIOD)"
    :is-disabled-reset-button="isDisabledResetButton"
    is-modal-only
    :modal-class="'filter-dates-modal with-chips'"
    :class="'filter-dates tour-dates'"
    :total-count="categoryStore.experiencesTmpCount"
    :is-loading="categoryStore.isExperiencesCountLoading"
    modal-no-padding
    @open="onPopupOpen"
    @close="onPopupClose"
    @apply="categoryStore.onApplyFilter(EExperienceFilterType.PERIOD)"
    @reset="categoryStore.onResetFilter(EExperienceFilterType.PERIOD)"
  >
    <div class="filter-weekdays ref-modal-fixed">
      <CalendarWeekdays />
    </div>

    <Calendar
      ref="calendarRef"
      :key="calendarKey"
      :default-selected-dates="categoryStore.defaultDates"
      no-weekdays
      class="filter-calendar"
      @select="onCalendarSelect"
    />

    <template #footer>
      <UiChips
        :options="displayChips"
        class="dates-chips"
        :modelValue="activeDateChip"
        emit-value
        small
        @update:modelValue="onServerChipSelect"
      />
    </template>
  </TravelersFilterPopup>
</template>

<script setup lang="ts">
  import { Calendar, CalendarWeekdays } from '@experience/core-calendar-ui';
  import { UiChips } from '@experience/core-chips-ui';
  import { useI18n } from '@experience/i18n';
  import { TravelersFilterPopup } from '@experience/travelers-filter-popup-package';
  import { computed, ref, type useShadowRoot, inject, h, watch, nextTick, onMounted, onBeforeUnmount } from 'vue';
  import ru from '../i18n/ru.json';
  import { useCategoryStore } from '@/shared/store';
  import { EExperienceFilterType, getPeriodFilterTitle } from '@/widgets/filtersBlock';
  import { applyModalStyles, resetModalStyles } from '@/shared/lib';
  import type { GetCalendarEventChipsResponse } from '@/shared/api/experience';
  import { ConfettyIcon } from '@/shared/ui';
  import { getFilterDate } from '../lib/title';

  type DateChipOption = {
    label: string;
    value: string;
    icon?: ReturnType<typeof h>;
  };

  type DateChipValue = string;

  const isDateChipValue = (value: unknown): value is DateChipValue => typeof value === 'string';
  const isDateChipValueArray = (value: unknown): value is DateChipValue[] =>
    Array.isArray(value) && value.every((item) => isDateChipValue(item));

  const { t } = useI18n({
    inheritLocale: true,
    messages: {
      ru,
    },
  });

  const rootNode = inject<ReturnType<typeof useShadowRoot>>('shadowRoot', null);

  const activeDateChip = ref<DateChipValue | null>(null);
  const calendarRef = ref<
    | (InstanceType<typeof Calendar> & {
        scrollToMonth: (year: number, month: number) => void;
      })
    | null
  >(null);

  const categoryStore = useCategoryStore();

  /**
   * [ref] текст кнопки фильтра
   */
  const title = computed(() => {
    if (!categoryStore.tmpFilters[EExperienceFilterType.PERIOD].start_date) {
      return t('default_title');
    }

    if (!categoryStore.tmpFilters[EExperienceFilterType.PERIOD].end_date) {
      return getPeriodFilterTitle(categoryStore.tmpFilters[EExperienceFilterType.PERIOD].start_date, '', t);
    }

    return getPeriodFilterTitle(
      categoryStore.tmpFilters[EExperienceFilterType.PERIOD].start_date,
      categoryStore.tmpFilters[EExperienceFilterType.PERIOD].end_date,
      t,
    );
  });
  const staticTitle = ref(t('title'));

  const updateMatch = () => {
    isSmallScreen.value = mql.matches;
  };

  const calendarKey = ref(1);

  const isDisabledResetButton = computed(
    () =>
      !categoryStore.tmpFilters[EExperienceFilterType.PERIOD].start_date &&
      !categoryStore.tmpFilters[EExperienceFilterType.PERIOD].end_date,
  );

  const parseYearMonth = (iso: string) => {
    const [yearStr, monthStr] = iso.split('-');

    return { year: Number(yearStr), monthIndex: Number(monthStr) - 1 };
  };

  const isSmallScreen = ref(false);
  const mql = window.matchMedia('(max-width: 360px)');

  const allPeriods = computed((): GetCalendarEventChipsResponse[] => [
    ...(categoryStore.calendarEventsChips?.public_holidays ?? []),
    ...(categoryStore.calendarEventsChips?.months ?? []),
    ...(categoryStore.calendarEventsChips?.seasons ?? []),
  ]);

  const holidayValues = computed<Set<string>>(
    () =>
      new Set(
        (categoryStore.calendarEventsChips?.public_holidays ?? []).map(
          ({ start_date, end_date }) => `${start_date}_${end_date}`,
        ),
      ),
  );

  const formattedDateChips = computed<DateChipOption[]>(() =>
    allPeriods.value.map((item) => {
      const val = `${item.start_date}_${item.end_date}`;

      const isHoliday = holidayValues.value.has(val);

      return {
        label: isHoliday ? getFilterDate(item.start_date, item.end_date, t('default_title')) : item.name,
        value: val,
        icon: isHoliday ? h(ConfettyIcon) : undefined,
      };
    }),
  );

  const displayChips = computed(() =>
    isSmallScreen.value ? formattedDateChips.value.slice(0, 6) : formattedDateChips.value,
  );

  const updateActiveChipAndScroll = () => {
    const from = categoryStore.tmpFilters[EExperienceFilterType.PERIOD].start_date;
    const to = categoryStore.tmpFilters[EExperienceFilterType.PERIOD].end_date;
    if (!from) {
      activeDateChip.value = null;

      return;
    }

    const val = `${from}_${to}`;
    activeDateChip.value = formattedDateChips.value.some((chip) => chip.value === val) ? val : null;

    const { year, monthIndex } = parseYearMonth(from);
    const today = new Date();
    if (year === today.getFullYear() && monthIndex === today.getMonth()) {
      return;
    }
    setTimeout(() => {
      calendarRef.value?.scrollToMonth(year, monthIndex);
    }, 300);
  };
  /**
   * [method] открытие модального окна
   */
  const onPopupOpen = async () => {
    applyModalStyles();
    updateActiveChipAndScroll();
  };
  /**
   * [method] закрытие модального окна
   */
  const onPopupClose = () => {
    resetModalStyles();
    categoryStore.onResetTmpFilter(EExperienceFilterType.PERIOD);
  };

  /**
   * [method] выбор дат в фильтре
   */
  function onCalendarSelect(from: string | null, to: string | null) {
    if (from === null && to === null) {
      return;
    }

    categoryStore.setPeriod(from, to);
  }

  watch(
    () => [
      categoryStore.tmpFilters[EExperienceFilterType.PERIOD].start_date,
      categoryStore.tmpFilters[EExperienceFilterType.PERIOD].end_date,
    ],
    ([from, to]) => {
      const match = formattedDateChips.value.find((chip) => {
        const [chipFrom, chipTo] = chip.value.split('_');

        return chipFrom === from && chipTo === to;
      });

      activeDateChip.value = match?.value ?? null;
    },
  );

  const onServerChipSelect = async (value: unknown) => {
    const selectedValue = isDateChipValueArray(value) ? value[0] : isDateChipValue(value) ? value : null;

    if (!selectedValue) {
      activeDateChip.value = null;
      categoryStore.setPeriod(null, null);
      calendarKey.value += 1;

      return;
    }

    const [fromIso, toIso] = selectedValue.split('_');

    if (!fromIso || !toIso) {
      return;
    }

    categoryStore.setPeriod(fromIso, toIso);

    calendarKey.value += 1;

    await nextTick();

    const { year, monthIndex } = parseYearMonth(fromIso);
    const today = new Date();
    if (!(year === today.getFullYear() && monthIndex === today.getMonth())) {
      calendarRef.value?.scrollToMonth(year, monthIndex);
    } else {
      const modal = rootNode?.querySelector<HTMLElement>('.filter-dates-modal');
      if (!modal) {
        return;
      }

      const scrollable = modal.querySelector<HTMLElement>('.ui-modal__scroll-container') ?? modal;

      scrollable.scrollTo({ top: 0, behavior: 'smooth' });
    }

    activeDateChip.value = formattedDateChips.value.some((chip) => chip.value === selectedValue) ? selectedValue : null;
  };

  onMounted(() => {
    mql.addEventListener('change', updateMatch);
    updateMatch();
  });

  onBeforeUnmount(() => {
    mql.removeEventListener('change', updateMatch);
  });
</script>

<style lang="scss">
  .filter-dates-modal {
    .ui-modal-header.ui-modal-header_fixed {
      &:after {
        display: none;
      }
    }
  }
</style>

<style scoped lang="scss">
  @use '@experience/shared/dist/assets/adaptivity' as adaptivity;
  @use '@experience/design-tokens/typography' as typography;

  .filter-dates {
    @include adaptivity.mobile {
      &:not(.tour-dates) {
        width: 100% !important;
      }

      :deep(.ui-chip) {
        justify-content: space-between;
        width: 100%;
        max-width: 100%;
      }

      :deep(.ui-chip__label) {
        text-align: left;
      }

      :deep(.filter-popup) {
        margin-top: 32px;
        border-top-left-radius: 16px;
        border-top-right-radius: 16px;
      }
    }
  }

  .filter-dates-modal {
    .filter-weekdays {
      --cell-height: 32px;
      position: relative;
      display: flex;
      justify-content: center;
      background-color: var(--alias-color-background-default);

      &:after {
        content: ' ';
        position: absolute;
        bottom: 0;
        left: 0;
        width: 100%;
        height: 1px;
        background-color: var(--alias-color-border-default);
      }
    }

    .dates-chips {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      max-height: none;
      padding: 8px 0 16px;
    }

    &.with-chips {
      .ui-modal-footer {
        padding-top: 8px;

        @include adaptivity.tablet {
          padding-top: 0;
        }
      }
    }
  }

  .experiment-block,
  .experiment-block-date-title {
    padding: 8px 16px;
  }

  .experiment-block-date-title {
    @include typography.base-500-400-semi;
  }
</style>
